import React, { useState } from 'react';
import { getCsrfToken as csrfToken } from '../lib/csrf.js';

export default function PromoCodeField({ appliedCode = null, appliedLabel = null, onApplied, onRemoved, disabled = false }) {
  const [open, setOpen] = useState(Boolean(appliedCode));
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const applied = Boolean(appliedCode);

  async function handleApply() {
    const trimmed = code.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      const response = await fetch('/api/buy/validate_promo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({ code: trimmed }),
      });
      const data = await response.json();
      if (data?.valid) {
        setCode('');
        await onApplied?.(data.code, data);
      } else {
        setError("That code isn't valid.");
      }
    } catch {
      setError('Could not check that code. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onRemoved?.();
    } finally {
      setBusy(false);
    }
  }

  if (applied) {
    return (
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="truncate text-green-700">
          <span className="font-medium">Promo:</span> {appliedCode}{appliedLabel ? ` (${appliedLabel})` : ''}
        </span>
        <button
          type="button"
          onClick={handleRemove}
          disabled={busy || disabled}
          className="shrink-0 font-medium text-gray-500 hover:text-gray-700 underline underline-offset-2 disabled:opacity-50 cursor-pointer"
        >
          {busy ? '…' : 'Remove'}
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="text-xs font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-50 cursor-pointer"
      >
        Have a promo code?
      </button>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex gap-1.5">
        <input
          type="text"
          autoComplete="off"
          autoCapitalize="characters"
          aria-label="Promo code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApply(); } }}
          placeholder="Promo code"
          disabled={busy || disabled}
          className="flex-1 min-w-0 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm placeholder:text-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none disabled:bg-gray-50"
        />
        <button
          type="button"
          onClick={handleApply}
          disabled={busy || disabled || !code.trim()}
          className="shrink-0 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed cursor-pointer"
        >
          {busy ? '…' : 'Apply'}
        </button>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
